import { z } from "zod";

import * as DbEnum from "../../generated/db-enum";
import { enumReadSchema } from "../../shared/core/enum-read.schema";

/**
 * What the tenant's own edge last answered when the agent's probe asked whether
 * an app's public host is actually routed.
 *
 * - `ROUTED` — a Traefik router matched and its ForwardAuth middleware answered.
 * - `UNROUTED` — **the alarm.** The app is running and the edge has no route to
 *   it. Every other value describes a normal world; this one is the platform
 *   reporting itself broken.
 * - `UNAVAILABLE` — nothing is serving the app, or the router node had not
 *   loaded its table yet. Normal and transient.
 * - `NO_SUCH_APP` — the edge has no app on that host. Normal between an app
 *   being created and the first edge-authz publish that names it.
 * - `UNKNOWN` — the probe could not be performed, or the answer was not one the
 *   edge writes. Inconclusive, never an alarm.
 *
 * **Deliberately disjoint from `VibeDeploymentStatus`.** An unroutable
 * deployment is still HEALTHY — its container is up and passing its checks — so
 * a shared vocabulary would overwrite that fact and make "the workload is fine
 * and the routing is not" unsayable. That conflation is NEX-2714 one layer up
 * from the bare 404 it produced.
 *
 * Absent (`null` on the app) means NEVER OBSERVED, which is the state of most
 * apps most of the time — the probe only asks about a healthy, settled
 * deployment. Nothing may read absence as health.
 */
// Sourced from the Prisma-generated enum values (single source of truth) so the
// API contract can never drift from the DB enum, matching `VibeAppVisibility`.
export const VibeAppEdgeReachabilitySchema = z.enum(DbEnum.VIBE_APP_EDGE_REACHABILITY_VALUES);

/** `VibeAppEdgeReachabilitySchema` as a reader parses it: a value this build does not list reads as unlisted. */
export const VibeAppEdgeReachabilityReadSchema = enumReadSchema(
  DbEnum.VIBE_APP_EDGE_REACHABILITY_VALUES
);
export type VibeAppEdgeReachability = z.infer<typeof VibeAppEdgeReachabilitySchema>;
